import { useEffect, useState, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'

import { RemoveAccountModal } from '../../accounts/components/remove-account'
import { RedactAccountModal } from '../../accounts/components/redact-account'
import { ReactivateAccountModal } from '../../accounts/components/reactivate-account'
import { SetTwoFaMandatoryModal } from '../../accounts/components/set-twofa-mandatory/SetTwoFaMandatoryModal'
import type { MemberType } from '../MemberProfile.types'

import { Button, Flex } from '@percent/lemonade'
import { Account, RemoveAdminModal, Reset2FAModal } from '@percent/domain/iam'
import {
  type AccountModifyAction,
  useAccountAccess,
  usePermissions,
  useReset2FAModal,
  useRemoveAdminModal,
  useRedactAccountModal
} from '@percent/admin-dashboard/common/hooks'
import { useDeleteAccountModal } from '@percent/admin-dashboard/common/hooks/useDeleteAccountModal'
import { useReactivateAccountModal } from '@percent/admin-dashboard/common/hooks/useReactivateAccountModal'
import { useServices } from '@percent/admin-dashboard/containers/service/ServiceContext'
import { useToast } from '@percent/admin-dashboard/containers/toast/ToastContext'
import { useMutation } from '@percent/utility'
import { DeleteMemberDialog } from '@percent/admin-dashboard/app/member/deleteMemberDialog/DeleteMemberDialog'

type UseMemberProfileActionsParams = {
  account: Account
  memberType: MemberType
  /** True when the account's partner is strict-SSO — suppresses resend-invitation. */
  isStrictSSOPartner?: boolean
  onRefresh: () => void
}

type MemberProfileActions = {
  /** Rendered once, anywhere in the tree. */
  modals: ReactNode
  /** Account-lifecycle buttons for the profile header (null when none apply). */
  headerActions: ReactNode | null
  /** 2FA buttons for the Security & verification card (null when none apply). */
  securityActions: ReactNode | null
  /** Remove-from-partner for the linked-partner card (null when N/A). */
  partnerActions: ReactNode | null
  /** Remove-admin for the Admin card (null when N/A). */
  adminActions: ReactNode | null
}

/**
 * Owns the account-action modal state and produces the action buttons, grouped by
 * where they belong on the profile: lifecycle (delete/redact/reactivate/resend)
 * in the header, 2FA next to the security fields, remove-from-partner in the
 * partner card, remove-admin in the admin card. Lifecycle and 2FA gating follows
 * the account's own tenant, as pbp-api's `domainsModifyingAccount` does, so an
 * account with no tenant also answers to `organisation_accounts`. The `general`
 * (typeless) profile only gets lifecycle + 2FA.
 */
export function useMemberProfileActions({
  account,
  memberType,
  isStrictSSOPartner,
  onRefresh
}: UseMemberProfileActionsParams): MemberProfileActions {
  const { t } = useTranslation()
  const { adminService } = useServices()
  const addToast = useToast()
  const { userHasPermissions } = usePermissions()
  const { canModifyAccount } = useAccountAccess()

  const { partnerId } = account
  const isInternalLike = memberType === 'goodstack' || memberType === 'general'

  const {
    selectedAccount: reset2FAAccount,
    isModalOpen: isReset2FAModalOpen,
    openModal: openReset2FAModal,
    closeModal: closeReset2FAModal,
    reset2FA
  } = useReset2FAModal({ onSuccess: onRefresh })

  const {
    selectedAccount: selectedAccountForAdminUpdate,
    isModalOpen: isRemoveAdminModalOpen,
    modalState: removeAdminModalState,
    errorMessage: removeAdminErrorMessage,
    openModal: openRemoveAdminModal,
    closeModal: closeRemoveAdminModal,
    confirmRemoveAdmin
  } = useRemoveAdminModal()

  const {
    selectedAccount: redactAccount,
    isModalOpen: isRedactModalOpen,
    openModal: openRedactModal,
    closeModal: closeRedactModal
  } = useRedactAccountModal()

  const {
    selectedAccount: deleteAccount,
    isModalOpen: isDeleteModalOpen,
    openModal: openDeleteModal,
    closeModal: closeDeleteModal
  } = useDeleteAccountModal()

  const {
    selectedAccount: reactivateAccount,
    isModalOpen: isReactivateModalOpen,
    openModal: openReactivateModal,
    closeModal: closeReactivateModal
  } = useReactivateAccountModal()

  const [isTwoFaMandatoryOpen, setIsTwoFaMandatoryOpen] = useState(false)
  const [isRemoveFromPartnerOpen, setIsRemoveFromPartnerOpen] = useState(false)

  const [{ status: resendStatus }, { mutateAsync: resendInvitation }] = useMutation(
    adminService.commands.createPartnerDeveloper
  )

  // useRemoveAdminModal has no success callback of its own, so refetch the
  // account once the admin flag has been cleared to keep the profile in sync.
  useEffect(() => {
    if (removeAdminModalState === 'success') {
      onRefresh()
    }
  }, [removeAdminModalState, onRefresh])

  useEffect(() => {
    if (resendStatus === 'success') {
      addToast(t('typography.invitationResent'), 'success')
    }
  }, [resendStatus, addToast, t])

  const can = (action: AccountModifyAction): boolean => canModifyAccount(account, action)

  // Visibility, mirroring each list view's gating.
  const canReactivate = can('can_reactivate') && account.status === 'removed' && !account.redactedAt
  const canDelete = can('can_delete') && account.status !== 'removed'
  // Hidden once already redacted — as a prominent header button, re-redacting an
  // already-redacted account is wrong.
  const canRedact = can('can_redact') && !account.redactedAt
  // Strict-SSO partner members authenticate via SSO, so resending an invitation
  // is meaningless — the partner members list hides it, and so do we.
  const canResend =
    memberType === 'partner' &&
    !!partnerId &&
    account.status === 'pending' &&
    !isStrictSSOPartner &&
    userHasPermissions('partner_accounts:can_invite')
  const canResetTwoFa = can('can_reset_2fa') && !!account.twoFaSecretVerifiedAt
  const canSetTwoFaMandatory = isInternalLike && userHasPermissions('authentication:can_manage_2fa')
  const canRemoveFromPartner =
    memberType === 'partner' && !!partnerId && userHasPermissions('partner_accounts:can_remove')
  const canRemoveAdmin =
    memberType === 'goodstack' && userHasPermissions('accounts:can_assign_admin') && !!account.admin

  const openReset2FA = () =>
    openReset2FAModal({ id: account.id, email: account.email, fullName: account.fullName ?? null })
  const openRedact = () => openRedactModal({ id: account.id, email: account.email, fullName: account.fullName ?? null })

  const hasHeaderActions = canResend || canReactivate || canDelete || canRedact
  const headerActions = hasHeaderActions ? (
    <Flex direction="row" gap="sm" align="center">
      {canResend && (
        <Button
          variant="secondary"
          size="small"
          data-testid="action-resend-invitation"
          onClick={() => partnerId && resendInvitation({ payload: { email: account.email }, partnerId })}
        >
          {t('menu.resend')}
        </Button>
      )}
      {canReactivate && (
        <Button
          variant="secondary"
          size="small"
          data-testid="action-reactivate"
          onClick={() => openReactivateModal(account)}
        >
          {t('account.modify.reactivate.button')}
        </Button>
      )}
      {canDelete && (
        <Button variant="reject" size="small" data-testid="action-delete" onClick={() => openDeleteModal(account)}>
          {t('account.modify.remove.button')}
        </Button>
      )}
      {canRedact && (
        <Button variant="reject" size="small" data-testid="action-redact" onClick={openRedact}>
          {t('account.modify.redact.button')}
        </Button>
      )}
    </Flex>
  ) : null

  const hasSecurityActions = canSetTwoFaMandatory || canResetTwoFa
  const securityActions = hasSecurityActions ? (
    <Flex direction="row" gap="sm" align="center">
      {canSetTwoFaMandatory && (
        <Button
          variant="secondary"
          size="small"
          data-testid="action-set-2fa-mandatory"
          onClick={() => setIsTwoFaMandatoryOpen(true)}
        >
          {t(`account.modify.set2FAMandatory.${account.twoFaMandatory ? 'disable' : 'enable'}.button`)}
        </Button>
      )}
      {canResetTwoFa && (
        <Button variant="secondary" size="small" data-testid="action-reset-2fa" onClick={openReset2FA}>
          {t('button.reset2FA')}
        </Button>
      )}
    </Flex>
  ) : null

  const partnerActions = canRemoveFromPartner ? (
    <Button
      variant="secondary"
      size="small"
      data-testid="action-remove-from-partner"
      onClick={() => setIsRemoveFromPartnerOpen(true)}
    >
      {t('menu.removeFromPartner')}
    </Button>
  ) : null

  const adminActions = canRemoveAdmin ? (
    <Button
      variant="secondary"
      size="small"
      data-testid="action-remove-admin"
      onClick={() => openRemoveAdminModal(account)}
    >
      {t('button.removeAdmin')}
    </Button>
  ) : null

  const modals = (
    <>
      {reset2FAAccount && (
        <Reset2FAModal
          account={reset2FAAccount}
          isOpen={isReset2FAModalOpen}
          onClose={closeReset2FAModal}
          onReset={reset2FA}
        />
      )}
      {selectedAccountForAdminUpdate && (
        <RemoveAdminModal
          isOpen={isRemoveAdminModalOpen}
          onClose={closeRemoveAdminModal}
          onConfirm={confirmRemoveAdmin}
          accountName={selectedAccountForAdminUpdate.fullName || selectedAccountForAdminUpdate.email}
          accountEmail={selectedAccountForAdminUpdate.email}
          state={removeAdminModalState}
          errorMessage={removeAdminErrorMessage}
        />
      )}
      {isInternalLike && (
        <SetTwoFaMandatoryModal
          isOpen={isTwoFaMandatoryOpen}
          onClose={() => setIsTwoFaMandatoryOpen(false)}
          onSuccess={onRefresh}
          account={account}
        />
      )}
      {deleteAccount && (
        <RemoveAccountModal
          isOpen={isDeleteModalOpen}
          onClose={closeDeleteModal}
          onSuccess={onRefresh}
          account={deleteAccount}
        />
      )}
      {redactAccount && (
        <RedactAccountModal
          isOpen={isRedactModalOpen}
          onClose={closeRedactModal}
          onSuccess={onRefresh}
          account={redactAccount}
        />
      )}
      {reactivateAccount && (
        <ReactivateAccountModal
          isOpen={isReactivateModalOpen}
          onClose={closeReactivateModal}
          onSuccess={onRefresh}
          account={reactivateAccount}
        />
      )}
      {memberType === 'partner' && partnerId && (
        <DeleteMemberDialog
          openDeleteModal={isRemoveFromPartnerOpen}
          onClose={() => setIsRemoveFromPartnerOpen(false)}
          memberEmail={account.email}
          memberId={account.id}
          partnerId={partnerId}
          refresh={onRefresh}
        />
      )}
    </>
  )

  return { modals, headerActions, securityActions, partnerActions, adminActions }
}
